import React from "react";
import { Box, OutlinedInput } from "@mui/material";
import { observer } from "mobx-react";
import ConfirmPopup from "../../../../../views/components/common/pop-up/ConfirmPopup";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { SWAYY_CREDIT_NOTE_REASON_OPTIONS } from "../../../../../models/SwayyInvoice/SwayyInvoiceFilters";
import { swayyColors, swayyFont } from "../../../theme/tokens";

interface SwayyCreateCreditNoteDialogProps {
  open: boolean;
  invoiceNumber?: string;
  reason: string;
  remarks: string;
  amount: string;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onReasonChange: (reason: string) => void;
  onRemarksChange: (remarks: string) => void;
  onAmountChange: (amount: string) => void;
  onConfirm: () => void;
}

const SwayyCreateCreditNoteDialog: React.FC<
  SwayyCreateCreditNoteDialogProps
> = ({
  open,
  invoiceNumber,
  reason,
  remarks,
  amount,
  loading = false,
  error = null,
  onClose,
  onReasonChange,
  onRemarksChange,
  onAmountChange,
  onConfirm,
}) => {
  const selectedReason = SWAYY_CREDIT_NOTE_REASON_OPTIONS.filter(
    (option) => option.id === reason,
  );

  return (
    <ConfirmPopup
      mode="swayy"
      open={open}
      title="Create credit note?"
      message={`Issue a credit note against ${invoiceNumber || "this invoice"}. PDF generation will be queued.`}
      cancelText="Cancel"
      confirmText={loading ? "Creating..." : "Create credit note"}
      loading={loading}
      confirmDisabled={!reason || !amount.trim()}
      error={error}
      onCancel={onClose}
      onConfirm={onConfirm}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <AppSearchDropdownMulti
          title="Select reason"
          isMultiSelect={false}
          list={SWAYY_CREDIT_NOTE_REASON_OPTIONS}
          value={selectedReason}
          local
          fontSize="12px"
          disabled={loading}
          handleSelect={(items) => onReasonChange(items[0]?.id || "refund")}
        />
        <Box>
          <CustomTypography
            variant="body2"
            color={swayyColors.deepGreen}
            fontWeight={600}
            sx={{ mb: 1, fontFamily: swayyFont }}
          >
            Amount
          </CustomTypography>
          <OutlinedInput
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
            placeholder="0.00"
            type="number"
            fullWidth
            disabled={loading}
            sx={{
              borderRadius: "10px",
              fontFamily: swayyFont,
              fontSize: 13,
              bgcolor: swayyColors.white,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: swayyColors.border,
              },
            }}
          />
        </Box>
        <Box>
          <CustomTypography
            variant="body2"
            color={swayyColors.deepGreen}
            fontWeight={600}
            sx={{ mb: 1, fontFamily: swayyFont }}
          >
            Remarks (optional)
          </CustomTypography>
          <OutlinedInput
            value={remarks}
            onChange={(e) => onRemarksChange(e.target.value)}
            placeholder="Add remarks"
            fullWidth
            multiline
            minRows={2}
            disabled={loading}
            sx={{
              borderRadius: "10px",
              fontFamily: swayyFont,
              fontSize: 13,
              bgcolor: swayyColors.white,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: swayyColors.border,
              },
            }}
          />
        </Box>
      </Box>
    </ConfirmPopup>
  );
};

export default observer(SwayyCreateCreditNoteDialog);
